import React, { useMemo, useCallback, useEffect } from 'react';
// import { RefreshControl } from 'react-native';
import { XStack, useTheme, Button } from 'tamagui';
import AddIcon from '@mui/icons-material/Add';
import { Virtuoso } from 'react-virtuoso';
import moment from 'moment';

import { CobuText, CobuSpacer, CobuStack } from '@cobu/ui';
import { GroupGatheringCard } from './GroupGatheringCard';
import { Group } from '@cobu/api-client';
import { GroupGathering } from '../../store/groupDetailSlice';
import { Strings } from '../../constants';
import { analyticsService } from '@cobu/analytics';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../utils/dateTime';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useResidentRestrictions } from '../../hooks/useResidentRestrictions';

interface GroupDetailInfoProps {
  group: Group;
  gatherings: GroupGathering[];
  members: any[];
  userIsGroupMember: boolean;
  onRefresh: () => void;
}

export const GroupDetailInfo: React.FC<GroupDetailInfoProps> = ({
  group,
  gatherings,
  userIsGroupMember,
  onRefresh,
}) => {
  const theme = useTheme();
  const [refreshing, setRefreshing] = React.useState(false);
  const strings = Strings.get();
  const navigate = useNavigate();
  const { residentsCanCreateEvents, showEvents } = useFeatureFlags();
  const { canCreateEvent } = useResidentRestrictions();
  const canCreateGathering = residentsCanCreateEvents && canCreateEvent;

  // Check if this is a marketplace group
  const isMarketplaceGroup = useMemo(() => group?.isMarketplace, [group?.isMarketplace]);

  // Memoize organized gatherings to avoid recalculation on every render
  const organizedGatherings = useMemo(() => {
    const now = moment();
    const result = {
      now: [] as GroupGathering[],
      upcoming: [] as GroupGathering[],
      past: [] as GroupGathering[],
    };

    gatherings.forEach((gathering) => {
      const startTime = moment(gathering.scheduledStartTime);
      const thirtyMinBefore = moment(startTime).subtract(30, 'minutes');
      const sixHrsAfter = moment(startTime).add(6, 'hours');

      if (now.isBetween(thirtyMinBefore, sixHrsAfter)) {
        result.now.push(gathering);
      } else if (now.isBefore(thirtyMinBefore)) {
        result.upcoming.push(gathering);
      } else {
        result.past.unshift(gathering);
      }
    });

    return result;
  }, [gatherings]);

  // Memoize refresh handler to prevent unnecessary re-renders
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  }, [onRefresh]);

  // Memoize create gathering handler
  const handleCreateGathering = useCallback(() => {
    if (!group || !canCreateGathering) return;
    navigate({
      pathname: '/events/create-group-event',
      search: new URLSearchParams({
        groupId: group.uuid,
        groupName: group.name,
      }).toString(),
    });
  }, [group?.name, group?.uuid, navigate, canCreateGathering]);

  // Memoize active gatherings check for performance
  const hasActiveGatherings = useMemo(() =>
    organizedGatherings.now.length > 0 || organizedGatherings.upcoming.length > 0,
    [organizedGatherings.now.length, organizedGatherings.upcoming.length]
  );

  useEffect(() => {
    analyticsService.trackEvent('VIEW_GROUP_EVENT_DETAILS');
  }, []);

  // Memoize item renderer for performance
  const renderGatheringItem = useCallback(({ item }: { item: GroupGathering }) => (
    <GroupGatheringCard
      gathering={item}
      onPress={() => {
        // Pass eventDate to ensure timing matches between list and detail pages
        // The detail page will handle 400 errors for non-recurring events by retrying without eventDate
        const eventDate = formatDateTime(
          item.scheduledStartTime,
          item.buildingTimezone as string,
          'YYYY-MM-DD'
        );
        navigate({
          pathname: `/events/${item.uuid}`,
          search: new URLSearchParams({
            eventDate,
            currentFilter: 'past',
          }).toString()
        });
      }}
    />
  ), []);

  const keyExtractor = useCallback((item: GroupGathering) => `${item.uuid}-${item.scheduledStartTime}`, []);

  // Don't show gatherings section if group is not loaded
  if (!group) {
    return null;
  }

  // Don't show gatherings section for non-members of private groups
  if (!userIsGroupMember && !group.isDefault) {
    return null;
  }

  // Don't show gatherings section for marketplace groups (same logic as old app)
  if (isMarketplaceGroup) {
    return <CobuSpacer size="$4" />;
  }

  if (!showEvents) return null;

  return (
    <CobuStack px="$4" py="$3">
      {hasActiveGatherings ? (
        <CobuStack>
          {/* Header with title and New button */}
          <CobuStack jc="space-between" ai="center" mb="$3" layout="horizontal">
            <CobuText
              variant="subheading"
              color="$cobuDarkBlue"
            >
              {strings.groupDetail.labels.gatherings}
            </CobuText>

            {canCreateGathering && (
            <Button
              size="$7"
              px={'$3'}
              bc="transparent"
              boc="$cobuPink50"
              bw={1}
              br="$full"
              pressStyle={{ background: '$cobuPink10' }}
              onPress={handleCreateGathering}
            >
                <CobuStack ai="center" gap="$1" layout="horizontal">
                  <AddIcon
                    width={16}
                    height={16}
                    style={{
                      color: '$cobuPink',
                    }}
                  />
                  <CobuText
                    variant="body"
                    color="$cobuPink"
                  >
                    {strings.groupDetail.labels.new}
                  </CobuText>
                </CobuStack>
            </Button>)}
          </CobuStack>

          {/* Now gatherings */}
          {organizedGatherings.now.length > 0 && (
            <CobuStack mb="$4">
              <CobuText
                variant="body"
                color="$cobuDarkBlue"
                mb="$2"
                tt="uppercase"
              >
                {strings.groupDetail.labels.now}
              </CobuText>
              <Virtuoso
                data={organizedGatherings.now}
                itemContent={(index, item) => (
                  <CobuStack px={4}>
                    {renderGatheringItem({ item: item as GroupGathering })}
                  </CobuStack>
                )}
                horizontalDirection
                computeItemKey={(index, item) => keyExtractor(item as GroupGathering)}
                style={{ width: '100%', height: 220 }}
              />
            </CobuStack>
          )}

          {/* Upcoming gatherings */}
          {organizedGatherings.upcoming.length > 0 && (
            <CobuStack>
              <CobuText
                variant="body"
                color="$cobuDarkBlue"
                mb="$2"
                tt="uppercase"
              >
                {strings.groupDetail.labels.upcoming}
              </CobuText>
              <Virtuoso
                data={organizedGatherings.upcoming}
                horizontalDirection
                itemContent={(index, item) => (
                  <CobuStack px={8}>
                    {renderGatheringItem({ item: item as GroupGathering })}
                  </CobuStack>
                )}
                computeItemKey={(index, item) => keyExtractor(item as GroupGathering)}
                style={{ width: '100%', height: 140 }}
              />
            </CobuStack>
          )}
        </CobuStack>
      ) : (
        <CobuStack
          mx="$4"
          py="$4"
          px="$4"
          bc="$backgroundSecondary"
          br="$2"
        >
          <CobuText
            variant="subheading"
            color="$cobuDarkBlue"
            mb="$2"
            ta="center"
            lh={21}
          >
            {strings.groupDetail.labels.gatheringsEmptyTitle}
          </CobuText>

          <CobuText
            variant="body"
            color="$backgroundMuted"
            ta="center"
            mb="$4"
            mx="$6"
            lh={21}
          >
            {Strings.format(
              strings.groupDetail.labels.gatheringsEmptySubtitle,
              { groupName: group?.name || '' }
            )}
          </CobuText>

          {canCreateGathering && (
            <Button
              mx="$8"
              mih="$10"
              h="auto"
              bc="$buttonBackground"
              br="$5"
              pressStyle={{ background: '$buttonBackground' }}
              hoverStyle={{ background: '$buttonBackground' }}
              focusStyle={{ background: '$buttonBackground' }}
              onPress={handleCreateGathering}
            >
              <CobuText
                variant="body"
                color="$white"
                lh={16}
              >
                {strings.groupDetail.labels.createGathering}
              </CobuText>
            </Button>)}
        </CobuStack>
      )}
    </CobuStack>
  );
}; 